import React, { useState, useEffect } from "react";
import { authFetch } from "../api";
import { calculateClosingCosts } from "../utils/closingCostsCalculator";

export default function ClosingCostsModal({ isOpen, onClose, property, askingPrice, hasFinancing = true, onSave, propertyId, checklistData }) {
  // State for dropdown selections
  const [saleType, setSaleType] = useState("Resale");
  const [buildingType, setBuildingType] = useState("Condo");
  const [financing, setFinancing] = useState("Yes"); // Default to Yes

  // State for config data
  const [configData, setConfigData] = useState(null);
  const [loadingConfig, setLoadingConfig] = useState(false);

  // State for costs
  const [costs, setCosts] = useState({
    applicationFee: 400,
    appraisalFee: 600,
    creditCheck: 250,
    bankAttorney: 750,
    recordingFee: 500,
    attorney: 0,
    workingCapital: 0,
    closerFee: 0,
    mansionTax: 0,
    mortgageRecordingTax: 0,
    titleInsurance: 0,
    mortgageTitleInsurance: 0,
    transferTaxes: 0,
    closingCostCredit: 0,
  });

  // State for dynamic "Other Costs"
  const [otherCosts, setOtherCosts] = useState([
    { id: 1, label: "Other", amount: 0 }
  ]);

  // Track if user made any changes
  const [userMadeChanges, setUserMadeChanges] = useState(false);

  // Track if we've loaded saved analysis data
  const [hasSavedData, setHasSavedData] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // State for save feedback
  const [saveStatus, setSaveStatus] = useState(null); // null | 'success'

  // State for transfer tax credit checkbox
  const [creditTransferTax, setCreditTransferTax] = useState(false);

  // Sync closing_costs type other costs from checklist when modal opens or checklist changes
  // Wait for initial load to complete to avoid race conditions with saved data
  useEffect(() => {

    // Only sync after initial load is complete to avoid overwriting saved data
    if (isOpen && !isInitialLoad && checklistData?.other_costs_items) {
      const closingCostsItems = checklistData.other_costs_items
        .filter(item => item.payment_type === "closing_costs" && item.name && item.cost)
        .map((item, index) => ({
          id: item.id || index + 100, // Use item.id or generate one
          label: item.name,
          amount: parseFloat(item.cost) || 0
        }));


      // Get currently saved user-added other costs (ID < 100)
      setOtherCosts(prevCosts => {
        const userCosts = prevCosts.filter(item => item.id < 100);

        // If no user costs, start with default
        const finalUserCosts = userCosts.length > 0 ? userCosts : [{ id: 1, label: "Other", amount: 0 }];

        // Merge user costs with checklist closing_costs items
        const merged = [...finalUserCosts, ...closingCostsItems];
        return merged;
      });
    } else if (isOpen && !isInitialLoad && !checklistData?.other_costs_items) {
      // No checklist items, just keep user-added costs
      setOtherCosts(prevCosts => {
        const userCosts = prevCosts.filter(item => item.id < 100);
        return userCosts.length > 0 ? userCosts : [{ id: 1, label: "Other", amount: 0 }];
      });
    }
  }, [isOpen, isInitialLoad, checklistData?.other_costs_items]);

  // Load closing costs config and saved property analysis on modal open
  useEffect(() => {
    if (isOpen) {
      setHasSavedData(false);
      setIsInitialLoad(true);
      const loadData = async () => {
        await loadConfig();
        await loadSavedAnalysis();
        setIsInitialLoad(false);
      };
      loadData();
    }
  }, [isOpen]);

  // Calculate costs when config loads or dropdowns change
  // Skip initial calculation if we have saved data (wait for saved data to load)
  useEffect(() => {
    if (configData && askingPrice) {
      // If this is initial load and we have saved data, skip calculation
      // The saved fees will be loaded by loadSavedAnalysis
      if (isInitialLoad && hasSavedData) {
        return;
      }

      calculateCosts();
    }
  }, [configData, askingPrice, saleType, buildingType, financing, isInitialLoad, hasSavedData, otherCosts]);

  const loadConfig = async () => {
    setLoadingConfig(true);
    try {
      const response = await authFetch("/config/closing_costs");
      setConfigData(response.data);
    } catch (error) {
      console.error("Failed to load closing costs config:", error);
    } finally {
      setLoadingConfig(false);
    }
  };

  const loadSavedAnalysis = async () => {
    if (!propertyId) {
      return;
    }


    try {
      const response = await authFetch(`/properties/${propertyId}/analysis`);

      if (response && response.analysis) {
        const analysis = response.analysis;

        // Check if we have saved modal-specific data (not just the defaults)
        const hasModalData = analysis.editable_fees || analysis.other_costs || analysis.sale_type;

        if (hasModalData) {
          setHasSavedData(true);

          // Load saved modal settings (these will trigger recalculation via useEffect)
          if (analysis.sale_type) {
            setSaleType(analysis.sale_type);
          }
          if (analysis.building_type) {
            setBuildingType(analysis.building_type);
          }
          if (analysis.financing) {
            setFinancing(analysis.financing);
          }

          // Load saved other costs
          if (analysis.other_costs) {
            setOtherCosts(analysis.other_costs);
          }

          // Load saved transfer tax credit checkbox state
          if (analysis.credit_transfer_tax !== undefined) {
            setCreditTransferTax(analysis.credit_transfer_tax);
          }

          // Store saved editable fees for later application
          // We'll apply them after the calculation runs
          if (analysis.editable_fees || analysis.closing_cost_credit !== undefined) {

            // Store the saved fees to apply after calculation
            window._savedEditableFees = analysis.editable_fees;
            window._savedClosingCostCredit = analysis.closing_cost_credit || 0;
          }
        } else {
        }
      } else {
      }
    } catch (error) {
    }
  };

  const calculateCosts = () => {
    if (!configData) return;


    // Check if we have saved editable fees to apply
    const savedEditableFees = window._savedEditableFees;
    const savedClosingCostCredit = window._savedClosingCostCredit;

    // Use shared utility for calculation
    const result = calculateClosingCosts({
      askingPrice,
      buildingType,
      saleType,
      financing,
      config: configData,
      editableFees: savedEditableFees || {}, // Use saved fees if available, otherwise config defaults
      otherCosts,
      closingCostCredit: savedClosingCostCredit !== undefined ? savedClosingCostCredit : costs.closingCostCredit
    });

    // Update costs state with calculated values
    setCosts({
      ...result.costs,
      closingCostCredit: savedClosingCostCredit !== undefined ? savedClosingCostCredit : costs.closingCostCredit
    });


    // Clear saved fees after applying them once
    if (savedEditableFees) {
      delete window._savedEditableFees;
      delete window._savedClosingCostCredit;
    }
  };

  // Handler to toggle transfer tax credit
  const handleTransferTaxCreditToggle = (isChecked) => {
    setUserMadeChanges(true);
    setCreditTransferTax(isChecked);

    if (isChecked) {
      // Add transfer tax to closing cost credit
      setCosts(prev => ({
        ...prev,
        closingCostCredit: prev.closingCostCredit + costs.transferTaxes
      }));
    } else {
      // Remove transfer tax from closing cost credit
      setCosts(prev => ({
        ...prev,
        closingCostCredit: Math.max(0, prev.closingCostCredit - costs.transferTaxes)
      }));
    }
  };

  // Handler to update individual cost field
  const updateCostField = (field, value) => {
    setUserMadeChanges(true); // Mark that user edited something
    setCosts(prev => ({
      ...prev,
      [field]: parseFloat(value) || 0
    }));
  };

  // Add new other cost item
  const addOtherCost = () => {
    setUserMadeChanges(true);
    const newId = Math.max(...otherCosts.map(c => c.id), 0) + 1;
    setOtherCosts([...otherCosts, { id: newId, label: "Other", amount: 0 }]);
  };

  // Remove other cost item
  const removeOtherCost = (id) => {
    setUserMadeChanges(true);
    if (otherCosts.length > 1) {
      setOtherCosts(otherCosts.filter(c => c.id !== id));
    }
  };

  // Update other cost label
  const updateOtherCostLabel = (id, label) => {
    setUserMadeChanges(true);
    setOtherCosts(otherCosts.map(c =>
      c.id === id ? { ...c, label } : c
    ));
  };

  // Update other cost amount
  const updateOtherCostAmount = (id, amount) => {
    setUserMadeChanges(true);
    setOtherCosts(otherCosts.map(c =>
      c.id === id ? { ...c, amount: parseFloat(amount) || 0 } : c
    ));
  };

  // Calculate totals
  const costsTotal = Object.entries(costs)
    .filter(([key]) => key !== "closingCostCredit")
    .reduce((sum, [_, value]) => sum + value, 0);

  const otherCostsTotal = otherCosts.reduce((sum, item) => sum + item.amount, 0);

  const totalBeforeCredit = costsTotal + otherCostsTotal;
  const netClosingCosts = totalBeforeCredit - costs.closingCostCredit;

  // Handle save button click
  const handleSave = () => {
    if (onSave) {
      // Filter out checklist-synced items (they have IDs from checklist and should not be saved)
      // Only save user-added "Other" costs (ID < 100)
      const userOtherCosts = otherCosts.filter(item => {
        // Keep items with ID < 100 (user-added items)
        // OR the default "Other" item (ID = 1)
        return item.id < 100;
      });

      onSave({
        netClosingCosts: Math.round(netClosingCosts),
        saleType,
        buildingType,
        financing,
        // Save editable values so they can be loaded back
        editableFees: {
          applicationFee: costs.applicationFee,
          appraisalFee: costs.appraisalFee,
          creditCheck: costs.creditCheck,
          bankAttorney: costs.bankAttorney,
          recordingFee: costs.recordingFee,
          attorney: costs.attorney,
          workingCapital: costs.workingCapital,
          closerFee: costs.closerFee,
        },
        closingCostCredit: costs.closingCostCredit,
        otherCosts: userOtherCosts,
        creditTransferTax: creditTransferTax
      });
    }
    setUserMadeChanges(false);

    // Show success message
    setSaveStatus('success');

    // Clear success message after 2 seconds
    setTimeout(() => {
      setSaveStatus(null);
    }, 2000);
  };

  // Handle save and close
  const handleSaveAndClose = () => {
    if (onSave) {
      // Filter out checklist-synced items (they have IDs from checklist and should not be saved)
      // Only save user-added "Other" costs (ID < 100)
      const userOtherCosts = otherCosts.filter(item => {
        // Keep items with ID < 100 (user-added items)
        // OR the default "Other" item (ID = 1)
        return item.id < 100;
      });

      onSave({
        netClosingCosts: Math.round(netClosingCosts),
        saleType,
        buildingType,
        financing,
        editableFees: {
          applicationFee: costs.applicationFee,
          appraisalFee: costs.appraisalFee,
          creditCheck: costs.creditCheck,
          bankAttorney: costs.bankAttorney,
          recordingFee: costs.recordingFee,
          attorney: costs.attorney,
          workingCapital: costs.workingCapital,
          closerFee: costs.closerFee,
        },
        closingCostCredit: costs.closingCostCredit,
        otherCosts: userOtherCosts,
        creditTransferTax: creditTransferTax
      });
    }
    setUserMadeChanges(false);
    onClose();
  };

  // Handle close without saving
  const handleClose = () => {
    setUserMadeChanges(false);
    setSaveStatus(null);
    onClose();
  };

  if (!isOpen) return null;

  // Styles
  const overlayStyle = {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.7)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 1000,
    padding: "20px",
  };

  const modalStyle = {
    backgroundColor: "white",
    borderRadius: "12px",
    padding: "32px",
    maxWidth: "800px",
    width: "100%",
    maxHeight: "90vh",
    overflowY: "auto",
    boxShadow: "0 10px 40px rgba(0,0,0,0.3)",
  };

  const headerStyle = {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "24px",
    borderBottom: "2px solid #000",
    paddingBottom: "16px",
  };

  const titleStyle = {
    fontSize: "24px",
    fontWeight: "700",
    color: "#333",
  };

  const closeButtonStyle = {
    fontSize: "28px",
    fontWeight: "700",
    cursor: "pointer",
    border: "none",
    background: "none",
    color: "#666",
    padding: "0",
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  };

  const sectionStyle = {
    marginBottom: "24px",
  };

  const sectionTitleStyle = {
    fontSize: "16px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "12px",
    borderBottom: "1px solid #ddd",
    paddingBottom: "8px",
  };

  const formGroupStyle = {
    marginBottom: "16px",
  };

  const labelStyle = {
    display: "block",
    fontSize: "14px",
    fontWeight: "600",
    color: "#333",
    marginBottom: "4px",
  };

  const inputStyle = {
    width: "100%",
    padding: "8px",
    border: "2px solid #ddd",
    borderRadius: "6px",
    fontSize: "14px",
    fontWeight: "600",
  };

  const selectStyle = {
    ...inputStyle,
    cursor: "pointer",
  };

  const costRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    borderBottom: "1px solid #eee",
    fontSize: "14px",
  };

  const summaryBoxStyle = {
    backgroundColor: "#f8f9fa",
    padding: "20px",
    borderRadius: "8px",
    marginTop: "24px",
    border: "2px solid #000",
  };

  const summaryRowStyle = {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    fontSize: "16px",
  };

  const totalRowStyle = {
    ...summaryRowStyle,
    fontWeight: "700",
    fontSize: "18px",
    borderTop: "2px solid #000",
    paddingTop: "12px",
    marginTop: "8px",
  };

  return (
    <div style={overlayStyle} onClick={handleClose}>
      <div style={modalStyle} onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div style={headerStyle}>
          <h2 style={titleStyle}>Closing Costs Breakdown</h2>
          <button style={closeButtonStyle} onClick={handleClose}>×</button>
        </div>

        {loadingConfig ? (
          <div style={{ textAlign: "center", padding: "40px" }}>Loading...</div>
        ) : (
          <>
            {/* Property Info Section */}
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Property Information</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "12px" }}>
                <div style={formGroupStyle}>
                  <label style={labelStyle}>Sale Type</label>
                  <select
                    style={selectStyle}
                    value={saleType}
                    onChange={(e) => setSaleType(e.target.value)}
                  >
                    <option value="Resale">Resale</option>
                    <option value="Sponsor">Sponsor</option>
                  </select>
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>Building Type</label>
                  <input
                    type="text"
                    style={{ ...inputStyle, backgroundColor: "#f8f9fa" }}
                    value={buildingType}
                    disabled
                  />
                </div>

                <div style={formGroupStyle}>
                  <label style={labelStyle}>Financing</label>
                  <input
                    type="text"
                    style={{ ...inputStyle, backgroundColor: "#f8f9fa" }}
                    value={financing}
                    disabled
                  />
                </div>
              </div>
            </div>

            {/* Fees Section */}
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Fees & Costs (Editable)</div>
              {[
                { label: "Application Fee", field: "applicationFee" },
                { label: "Appraisal Fee", field: "appraisalFee" },
                { label: "Credit Check", field: "creditCheck" },
                { label: "Bank Attorney", field: "bankAttorney" },
                { label: "Recording Fee", field: "recordingFee" },
                { label: "Attorney", field: "attorney" },
                { label: "Working Capital", field: "workingCapital" },
                { label: "Closer Fee", field: "closerFee" },
              ].map(({ label, field }) => (
                <div key={field} style={{ ...costRowStyle, alignItems: "center" }}>
                  <span>{label}</span>
                  <input
                    type="number"
                    style={{
                      width: "120px",
                      padding: "4px 8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px",
                      textAlign: "right",
                      fontWeight: "600",
                    }}
                    value={costs[field]}
                    onChange={(e) => updateCostField(field, e.target.value)}
                    min="0"
                    step="1"
                  />
                </div>
              ))}
            </div>

            {/* Taxes Section */}
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Taxes</div>
              <div style={costRowStyle}>
                <span>Mansion Tax</span>
                <strong>${costs.mansionTax.toLocaleString()}</strong>
              </div>
              <div style={costRowStyle}>
                <span>Mortgage Recording Tax</span>
                <strong>${costs.mortgageRecordingTax.toLocaleString()}</strong>
              </div>
              {saleType === "Sponsor" && (
                <div style={{ ...costRowStyle, alignItems: "center", display: "flex", justifyContent: "space-between" }}>
                  <span>Transfer Taxes</span>
                  <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                    <strong>${costs.transferTaxes.toLocaleString()}</strong>
                    {costs.transferTaxes > 0 && (
                      <label style={{ display: "flex", alignItems: "center", gap: "6px", fontSize: "13px", fontWeight: "600", color: "#0066cc", cursor: "pointer", marginBottom: 0 }}>
                        <input
                          type="checkbox"
                          checked={creditTransferTax}
                          onChange={(e) => handleTransferTaxCreditToggle(e.target.checked)}
                          style={{ cursor: "pointer" }}
                        />
                        Credit full amount
                      </label>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Insurance Section */}
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Insurance</div>
              <div style={costRowStyle}>
                <span>Title Insurance</span>
                <strong>${costs.titleInsurance.toLocaleString()}</strong>
              </div>
              <div style={costRowStyle}>
                <span>Mortgage Title Insurance</span>
                <strong>${costs.mortgageTitleInsurance.toLocaleString()}</strong>
              </div>
            </div>

            {/* Other Costs Section */}
            <div style={sectionStyle}>
              <div style={sectionTitleStyle}>Other Costs</div>
              {otherCosts.map((item) => (
                <div key={item.id} style={{ ...costRowStyle, alignItems: "center", gap: "8px" }}>
                  <input
                    type="text"
                    placeholder="Label"
                    style={{
                      flex: "1",
                      padding: "4px 8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px",
                      fontWeight: "600",
                    }}
                    value={item.label}
                    onChange={(e) => updateOtherCostLabel(item.id, e.target.value)}
                  />
                  <input
                    type="number"
                    style={{
                      width: "120px",
                      padding: "4px 8px",
                      border: "1px solid #ddd",
                      borderRadius: "4px",
                      fontSize: "14px",
                      textAlign: "right",
                      fontWeight: "600",
                    }}
                    value={item.amount}
                    onChange={(e) => updateOtherCostAmount(item.id, e.target.value)}
                    min="0"
                    step="1"
                  />
                  {otherCosts.length > 1 && (
                    <button
                      onClick={() => removeOtherCost(item.id)}
                      style={{
                        border: "none",
                        background: "#dc3545",
                        color: "white",
                        borderRadius: "4px",
                        width: "24px",
                        height: "24px",
                        cursor: "pointer",
                        fontSize: "16px",
                        fontWeight: "700",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      ×
                    </button>
                  )}
                </div>
              ))}
              <button
                onClick={addOtherCost}
                style={{
                  marginTop: "8px",
                  padding: "8px 16px",
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                + Add Another
              </button>
            </div>

            {/* Summary */}
            <div style={summaryBoxStyle}>
              <div style={summaryRowStyle}>
                <span>Total Before Credit</span>
                <strong>${totalBeforeCredit.toLocaleString()}</strong>
              </div>
              <div style={{ ...summaryRowStyle, alignItems: "center", gap: "8px" }}>
                <span>Closing Cost Credit</span>
                <input
                  type="number"
                  style={{
                    width: "120px",
                    padding: "4px 8px",
                    border: "1px solid #ddd",
                    borderRadius: "4px",
                    fontSize: "14px",
                    textAlign: "right",
                    fontWeight: "600",
                  }}
                  value={costs.closingCostCredit}
                  onChange={(e) => updateCostField("closingCostCredit", e.target.value)}
                  min="0"
                  step="1"
                />
              </div>
              <div style={totalRowStyle}>
                <span>Net Closing Costs</span>
                <span style={{ color: "#28a745" }}>${netClosingCosts.toLocaleString()}</span>
              </div>
            </div>

            {/* Save Status Message */}
            {saveStatus === 'success' && (
              <div style={{
                marginTop: "16px",
                padding: "12px",
                backgroundColor: "#d4edda",
                color: "#155724",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "600",
                display: "flex",
                alignItems: "center",
                gap: "8px",
              }}>
                <span style={{ fontSize: "18px" }}>✓</span>
                <span>Saved successfully!</span>
              </div>
            )}

            {/* Save Buttons */}
            <div style={{ marginTop: "24px", display: "flex", justifyContent: "flex-end", gap: "12px" }}>
              <button
                onClick={handleClose}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#6c757d",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSave}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#28a745",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Save
              </button>
              <button
                onClick={handleSaveAndClose}
                style={{
                  padding: "12px 24px",
                  backgroundColor: "#000",
                  color: "white",
                  border: "none",
                  borderRadius: "6px",
                  fontSize: "16px",
                  fontWeight: "600",
                  cursor: "pointer",
                }}
              >
                Save and Close
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
